<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-graduation-cap"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Training Links</div>
          <div class="panel-header__sub">Manage training content and resource links shown to users</div>
        </div>
      </div>

      <!-- Body -->
      <div class="panel-body">

        <div class="body-loading" v-if="loading">
          <div class="body-loading__spinner"></div>
          <span>Loading content…</span>
        </div>

        <template v-else>
          <div class="editor-wrap">
            <editor
              api-key="no-api-key"
              v-model="link"
              :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js"
            />
            <span class="err-msg" v-if="errors['link']">{{ errors['link'][0] }}</span>
          </div>
        </template>

      </div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar" v-if="!loading">
      <span class="save-bar__hint">
        <i class="fa fa-info-circle"></i> Changes are saved when you click Update
      </span>
      <button
        class="save-bar__update"
        ref="kt_training_link_submit"
        @click="update('kt_training_link_submit')"
      >
        <i class="fa fa-save"></i> Update
      </button>
    </div>

  </div>
</template>

<script>
import {
  FETCH_TRAINING_LINK,
  SUBMIT_TRAINING_LINK
} from "@/core/services/store/actions.type";
import Editor from "@tinymce/tinymce-vue";

export default {
  components: { editor: Editor },
  data() {
    return {
      loading: true,
      link: "",
      id: 0,
      errors: [],
      config: {
        license_key: "gpl",
        height: 520,
        menubar: false,
        plugins: [
          "advlist autolink lists link image charmap print preview anchor",
          "searchreplace visualblocks code fullscreen",
          "insertdatetime media table paste code help wordcount"
        ],
        toolbar:
          "undo redo | formatselect | fontsizeselect | bold italic backcolor | " +
          "alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | removeformat | code | help",
        fontsize_formats:
          "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt"
      }
    };
  },
  created() {
    this.$store.dispatch(FETCH_TRAINING_LINK).then(data => {
      const d = data.data;
      this.link    = d.link || "";
      this.id      = d.id   || 0;
      this.loading = false;
    }).catch(() => { this.loading = false; });
  },
  methods: {
    update(ref) {
      this.errors = [];
      const btn = this.$refs[ref];
      btn.disabled = true;
      btn.classList.add("spinner", "spinner-light", "spinner-right");
      this.$store.dispatch(SUBMIT_TRAINING_LINK, { link: this.link, id_: this.id })
        .then(data => {
          this.id = data.id;
          this.$toastr.s(data.msg);
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
        })
        .catch(err => {
          this.errors = err.msg;
          btn.classList.remove("spinner", "spinner-light", "spinner-right");
          btn.disabled = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$border: #dde2ec;

@keyframes spin { to { transform: rotate(360deg); } }

.page-wrap {
  padding: 12px 12px 80px;
  font-family: $font;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text { flex: 1; }
  &__title { font-size: 16px; font-weight: 700; color: $dark; line-height: 1.2; }
  &__sub   { font-size: 12px; color: #7a8499; margin-top: 3px; }
}

/* ── body ── */
.panel-body {
  padding: 24px;
}

/* ── loading ── */
.body-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 48px 0;
  font-size: 13px;
  color: #7a8499;

  &__spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid #c5d8f5;
    border-top-color: $blue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    flex-shrink: 0;
  }
}

/* ── editor ── */
.editor-wrap {
  border: 1.5px solid $border;
  border-radius: 10px;
  overflow: hidden;
}

.err-msg {
  display: block;
  font-size: 11px;
  color: #b42318;
  margin-top: 6px;
}

/* ── floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13,27,62,0.3);
  z-index: 100;

  &__hint {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: rgba(255,255,255,0.55);
    i { font-size: 12px; }
  }

  &__update {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $blue;
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover { background: #2d85bb; box-shadow: 0 4px 14px rgba(56,154,212,0.4); }
    &:disabled { opacity: 0.6; cursor: default; box-shadow: none; }
  }
}
</style>
